import React, { useState, useEffect, useMemo } from 'react';
import { ProjectStatus, BitumenGrade, Country } from '../types';
import { TANZANIA_REGIONS, UGANDA_DISTRICTS, KENYA_COUNTIES } from '../constants';
import { X, Plus, Sparkles, Loader2, Droplet, Trash2, AlertCircle, Package, FileText, Activity, Clock, Check, RefreshCcw } from 'lucide-react';
import { generateSmartUpdate } from '../services/geminiService';
import type { Project, ProjectUpdate, BitumenSpec } from '../types';

interface ProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (project: Project) => void;
  editingProject?: Project | null;
  activeCountry: Country;
}

export const ProjectModal: React.FC<ProjectModalProps> = ({ isOpen, onClose, onSubmit, editingProject, activeCountry }) => {
  const [formData, setFormData] = useState<Partial<Project>>({
    status: ProjectStatus.PLANNING,
    progress: 0,
    updates: [],
    bitumenSpecs: [],
    tenderNumber: ''
  });
  const [newUpdateText, setNewUpdateText] = useState('');
  const [isRefining, setIsRefining] = useState(false);

  // State for adding new Bitumen Spec
  const [tempBitumenGrade, setTempBitumenGrade] = useState('');
  const [tempBitumenQty, setTempBitumenQty] = useState('');

  // Use all predefined Bitumen Grades from types
  const bitumenGrades = Object.values(BitumenGrade);

  // Helper to resolve region list based on current country
  const getRegionsByCountry = (country: Country) => {
    switch (country) {
      case Country.UGANDA: return UGANDA_DISTRICTS;
      case Country.KENYA: return KENYA_COUNTIES;
      case Country.TANZANIA:
      default: return TANZANIA_REGIONS;
    }
  };

  // Determine current regional context
  const currentRegions = useMemo(() => {
    const country = editingProject?.country || activeCountry;
    return getRegionsByCountry(country);
  }, [editingProject, activeCountry]);

  useEffect(() => {
    if (editingProject) {
      setFormData({
        ...editingProject,
        bitumenSpecs: editingProject.bitumenSpecs || []
      });
    } else {
      // Reset form for new project initialization
      setFormData({
        id: crypto.randomUUID(),
        status: ProjectStatus.PLANNING,
        progress: 0,
        updates: [],
        bitumenSpecs: [],
        tenderNumber: '',
        name: '',
        contractor: '',
        region: currentRegions[0] || '',
        country: activeCountry,
        kilometers: 0,
        financer: '',
        consultant: '',
        startDate: '',
        estimatedCompletion: ''
      });
    }
    setNewUpdateText('');
    setTempBitumenGrade('');
    setTempBitumenQty('');
  }, [editingProject, isOpen, activeCountry, currentRegions]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'kilometers' || name === 'progress' ? Number(value) : value
    }));
  };

  const handleAddBitumen = () => {
    const qty = Number(tempBitumenQty);
    if (!tempBitumenGrade || isNaN(qty) || qty <= 0) {
      alert("Please select a grade and enter a positive quantity.");
      return;
    }
    
    const newSpec: BitumenSpec = {
      id: crypto.randomUUID(),
      grade: tempBitumenGrade,
      quantity: qty
    };

    setFormData(prev => ({
      ...prev,
      bitumenSpecs: [...(prev.bitumenSpecs || []), newSpec]
    }));

    setTempBitumenGrade('');
    setTempBitumenQty('');
  };

  const removeBitumen = (id: string) => {
    setFormData(prev => ({
      ...prev,
      bitumenSpecs: (prev.bitumenSpecs || []).filter(b => b.id !== id)
    }));
  };

  const handleAddUpdate = () => {
    if (!newUpdateText.trim()) return;
    const update: ProjectUpdate = {
      id: crypto.randomUUID(),
      date: new Date().toISOString().split('T')[0],
      content: newUpdateText,
      author: 'System User'
    };
    setFormData(prev => ({
      ...prev,
      updates: [...(prev.updates || []), update]
    }));
    setNewUpdateText('');
  };

  const handleSmartRefine = async () => {
    if (!newUpdateText.trim()) return;
    setIsRefining(true);
    try {
        const tempProject = { name: formData.name || 'Project', region: formData.region || 'Tanzania' } as Project;
        const refined = await generateSmartUpdate(tempProject, newUpdateText);
        setNewUpdateText(refined);
    } catch (e) {
        console.error("AI Refinement Error:", e);
    } finally {
        setIsRefining(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name && formData.contractor) {
      // Ensure branch integrity by forcing current active country for new records
      const submissionData = {
          ...formData,
          country: formData.country || activeCountry
      } as Project;
      onSubmit(submissionData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">
            {editingProject ? 'Edit Project Identity' : 'Register New Project'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-grow overflow-y-auto p-6 space-y-8 custom-scrollbar">
          
          <div className="space-y-6">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <FileText size={14} /> General Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Name</label>
                <input
                    required
                    type="text"
                    name="name"
                    value={formData.name || ''}
                    onChange={handleChange}
                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all"
                    placeholder="e.g. Kampala Expressway Extension"
                />
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Geographic Region ({formData.country})</label>
                <select
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 outline-none"
                >
                    {currentRegions.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project Status</label>
                <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 outline-none"
                >
                    {Object.values(ProjectStatus).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                </div>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Package size={14} /> Stakeholders & Scope
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Contractor</label>
                <input
                    required
                    type="text"
                    name="contractor"
                    value={formData.contractor || ''}
                    onChange={handleChange}
                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 outline-none"
                />
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial Agency</label>
                <input
                    type="text"
                    name="financer"
                    value={formData.financer || ''}
                    onChange={handleChange}
                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 outline-none"
                />
                </div>
                
                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consulting Engineer</label>
                <input
                    type="text"
                    name="consultant"
                    value={formData.consultant || ''}
                    onChange={handleChange}
                    className="w-full rounded-xl border-gray-300 border px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 outline-none"
                />
                </div>

                <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Distance (km)</label>
                <input
                    type="number"
                    name="kilometers"
                    step="0.1"
                    min="0"
                    value={formData.kilometers || 0}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:ring-4 focus:ring-blue-500/10 outline-none"
                />
                </div>
            </div>
          </div>

          {/* Bitumen Specs Section */}
          <div className="space-y-6 border-t border-gray-100 pt-8">
            <div className="flex justify-between items-center">
                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                <Droplet size={14} className="text-indigo-500" /> Material Specifications
                </h3>
                {formData.bitumenSpecs && formData.bitumenSpecs.length > 0 && (
                    <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">
                        Total: {formData.bitumenSpecs.reduce((sum, s) => sum + s.quantity, 0).toLocaleString()} MT
                    </span>
                )}
            </div>
            
            <div className="bg-slate-50 p-6 rounded-2xl space-y-6 border border-slate-100 shadow-inner">
              {/* List existing specs */}
              {formData.bitumenSpecs && formData.bitumenSpecs.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                  {formData.bitumenSpecs.map((spec) => (
                    <div key={spec.id} className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200 shadow-sm transition-all hover:border-indigo-300 group">
                       <div className="flex flex-col">
                         <span className="text-[10px] font-black text-indigo-600 uppercase tracking-tight">{spec.grade} Grade</span>
                         <span className="text-sm font-black text-slate-800">{spec.quantity.toLocaleString()} Metric Tons</span>
                       </div>
                       <button
                        type="button"
                        onClick={() => removeBitumen(spec.id)}
                        className="text-slate-300 hover:text-red-500 p-2 hover:bg-red-50 rounded-lg transition-all"
                       >
                         <Trash2 size={16} />
                       </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new spec inputs */}
              <div className="flex flex-col sm:flex-row gap-3 items-end">
                <div className="flex-grow w-full">
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Grade Identity</label>
                  <div className="relative">
                    <select
                        value={tempBitumenGrade}
                        onChange={(e) => setTempBitumenGrade(e.target.value)}
                        className="w-full rounded-xl border-gray-300 border px-4 py-2.5 text-sm font-bold text-slate-700 focus:ring-4 focus:ring-blue-500/10 outline-none bg-white transition-all appearance-none cursor-pointer"
                    >
                        <option value="" disabled>Select Bitumen Grade</option>
                        {bitumenGrades.map(grade => (
                        <option key={grade} value={grade}>{grade}</option>
                        ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                        <Plus size={14} />
                    </div>
                  </div>
                </div>
                <div className="w-full sm:w-40">
                   <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 ml-1">Qty (MT)</label>
                   <input
                    type="number"
                    value={tempBitumenQty}
                    onChange={(e) => setTempBitumenQty(e.target.value)}
                    placeholder="0.00"
                    min="1"
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-black focus:ring-4 focus:ring-blue-500/10 outline-none transition-all"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAddBitumen}
                  disabled={!tempBitumenGrade || !tempBitumenQty || Number(tempBitumenQty) <= 0}
                  className="px-6 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 disabled:opacity-30 transition-all flex items-center justify-center gap-2 h-[42px]"
                >
                  <Plus size={16} /> Add Line
                </button>
              </div>
              {tempBitumenQty && Number(tempBitumenQty) <= 0 && (
                  <p className="text-[10px] text-red-500 flex items-center gap-1 font-bold italic ml-1">
                      <AlertCircle size={12} /> Quantity must be a valid positive number
                  </p>
              )}
            </div>
          </div>

          {/* Updates Section */}
          <div className="space-y-6 border-t border-gray-100 pt-8">
            <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
              <Activity size={14} /> Operational Updates
            </h3>
            
            <div className="space-y-4">
               <textarea
                    value={newUpdateText}
                    onChange={(e) => setNewUpdateText(e.target.value)}
                    placeholder="Log site progress or technical challenges..."
                    className="w-full rounded-2xl border-gray-300 border p-5 focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none resize-none h-32 text-sm leading-relaxed transition-all bg-slate-50/50"
                />
              <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={handleSmartRefine}
                    disabled={isRefining || !newUpdateText.trim()}
                    className="flex items-center gap-2 px-6 py-2.5 bg-white text-indigo-600 border border-indigo-200 rounded-xl text-xs font-black uppercase tracking-widest shadow-sm hover:bg-indigo-50 disabled:opacity-50 transition-all active:scale-95"
                >
                    {isRefining ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                    Refine with AI
                </button>
                <button
                    type="button"
                    onClick={handleAddUpdate}
                    disabled={!newUpdateText.trim() || isRefining}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-100 hover:bg-indigo-700 disabled:opacity-30 transition-all active:scale-95"
                >
                    <Plus size={16} /> Append Update
                </button>
              </div>
            </div>

            <div className="space-y-4 bg-slate-50 p-6 rounded-2xl max-h-60 overflow-y-auto custom-scrollbar border border-slate-100 shadow-inner">
                {formData.updates && formData.updates.length > 0 ? (
                     [...formData.updates].reverse().map((update) => (
                        <div key={update.id} className="text-sm bg-white p-4 rounded-xl border border-gray-200 shadow-sm relative overflow-hidden group">
                            <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-30"></div>
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{update.date}</span>
                                <span className="text-[10px] font-black text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-lg uppercase">{update.author}</span>
                            </div>
                            <p className="text-slate-700 font-medium leading-relaxed">{update.content}</p>
                        </div>
                     ))
                ) : (
                    <div className="py-12 flex flex-col items-center justify-center text-slate-300">
                        <Clock size={32} className="opacity-20 mb-2" />
                        <p className="text-[10px] font-black uppercase tracking-widest">No updates logged</p>
                    </div>
                )}
            </div>
          </div>

        </form>

        <footer className="p-8 border-t border-gray-100 flex justify-end items-center gap-4 bg-slate-50 rounded-b-3xl shrink-0">
          <button
            onClick={onClose}
            className="px-8 py-3 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 rounded-2xl transition-all"
          >
            Discard
          </button>
          <button
            onClick={handleSubmit}
            className="px-12 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-3"
          >
            <Check size={18} />
            {editingProject ? 'Commit Changes' : 'Initialize Project'}
          </button>
        </footer>
      </div>
    </div>
  );
};